import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Lock, ChevronRight, CheckCircle, FileText, 
  Video, Headphones, BookOpen, Target, Crown, Loader2, Users, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Import Assertivita components
import AssertivitaPage from "./AssertivitaPage";

// Percorsi disponibili
const PERCORSI = [
  {
    id: "assertivita",
    title: "Training Assertività",
    subtitle: "Impara a comunicare con chiarezza e rispetto",
    icon: "🎯",
    color: "from-indigo-500 to-purple-600",
    audience: "Per chi vuole esprimersi senza aggressività né passività",
    tools: [
      { id: "sib25", name: "Test SIB-25", description: "Valuta il tuo stile comunicativo" },
      { id: "compass", name: "Test COMPASS", description: "30 situazioni per allenarti" },
      { id: "scheda", name: "Scheda Episodio", description: "Analizza conflitti e comportamenti" },
    ],
    highlights: ["3 strumenti pratici", "Esercizi guidati", "Schede scaricabili"],
    available: true
  },
  {
    id: "autostima",
    title: "Costruire l'Autostima",
    subtitle: "Rafforza la fiducia in te stesso",
    icon: "💪",
    color: "from-amber-500 to-orange-600",
    audience: "Per chi vuole sentirsi più sicuro nelle sfide quotidiane",
    tools: [],
    highlights: [],
    available: false,
    comingSoon: true
  },
  {
    id: "ansia",
    title: "Gestione dell'Ansia",
    subtitle: "Tecniche pratiche per ritrovare la calma",
    icon: "🧘",
    color: "from-teal-500 to-cyan-600",
    audience: "Per chi vuole gestire meglio stress e preoccupazioni",
    tools: [],
    highlights: [],
    available: false,
    comingSoon: true
  }
];

const PercorsiPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePercorso, setActivePercorso] = useState(null);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const response = await api.get('/stripe/subscription-status');
      setSubscription(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error checking subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasAccess = subscription?.is_active;

  // If viewing a specific percorso
  if (activePercorso === "assertivita" && hasAccess) {
    return (
      <div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setActivePercorso(null)}
          className="mb-4"
        >
          ← Torna ai Percorsi
        </Button>
        <AssertivitaPage />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in pb-24 md:pb-8" data-testid="percorsi-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h1 className="page-title">Percorsi</h1>
          {hasAccess && (
            <Badge className="bg-gradient-to-r from-indigo-500 to-purple-600 text-xs">
              <Crown className="w-3 h-3 mr-1" /> Premium
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground">
          Percorsi guidati per la tua crescita personale
        </p>
      </div>

      {/* Percorsi Cards - Value First */}
      <div className="space-y-4">
        {PERCORSI.map((percorso) => {
          const isLocked = !hasAccess && percorso.available;
          const isComingSoon = percorso.comingSoon;
          
          return (
            <Card 
              key={percorso.id}
              className={`border shadow-sm overflow-hidden transition-all ${
                isComingSoon ? 'opacity-60' : 'hover:shadow-md'
              } ${!isLocked && percorso.available ? 'cursor-pointer' : ''}`}
              onClick={() => {
                if (!isLocked && percorso.available) {
                  setActivePercorso(percorso.id);
                } else if (isLocked) {
                  toast.info("Attiva l'abbonamento o usa un codice per accedere");
                }
              }}
            >
              {/* Header gradient */}
              <div className={`h-1.5 bg-gradient-to-r ${percorso.color}`} />
              
              <CardContent className="p-4 sm:p-5">
                {/* Main info - Always visible */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${percorso.color} flex items-center justify-center shrink-0`}>
                    <span className="text-2xl">{percorso.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-lg">{percorso.title}</h3>
                        <p className="text-sm text-muted-foreground">{percorso.subtitle}</p>
                      </div>
                      {isComingSoon && (
                        <Badge variant="outline" className="text-xs shrink-0">
                          Prossimamente
                        </Badge>
                      )}
                      {isLocked && !isComingSoon && (
                        <Lock className="w-5 h-5 text-muted-foreground shrink-0" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Audience */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Users className="w-4 h-4" />
                  <span>{percorso.audience}</span>
                </div>
                
                {/* Tools and highlights */}
                {percorso.tools.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {percorso.highlights.map((highlight) => (
                        <div key={`highlight-${highlight}`} className="flex items-center gap-1.5 text-xs bg-muted/50 rounded-full px-2.5 py-1">
                          <Sparkles className="w-3 h-3 text-indigo-500" />
                          <span>{highlight}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {percorso.tools.map((tool) => (
                        <div 
                          key={tool.id}
                          className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{tool.name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action button */}
                {percorso.available && (
                  <div className="mt-4 pt-3 border-t">
                    {hasAccess ? (
                      <Button className={`w-full bg-gradient-to-r ${percorso.color}`}>
                        Inizia il percorso <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Link to="/impostazioni" className="flex-1">
                          <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-600">
                            <Crown className="w-4 h-4 mr-2" />
                            Sblocca - €4,99/mese
                          </Button>
                        </Link>
                        <Link to="/impostazioni">
                          <Button variant="outline" className="w-full sm:w-auto">
                            Ho un codice
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Materials Section (for premium users) */}
      {hasAccess && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Materiali
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button className="flex flex-col items-center gap-2 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                <Video className="w-5 h-5 text-indigo-500" />
                <span className="text-xs font-medium">Video</span>
                <span className="text-[10px] text-muted-foreground">Prossimamente</span>
              </button>
              <button className="flex flex-col items-center gap-2 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                <Headphones className="w-5 h-5 text-purple-500" />
                <span className="text-xs font-medium">Audio</span>
                <span className="text-[10px] text-muted-foreground">Prossimamente</span>
              </button>
              <button className="flex flex-col items-center gap-2 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                <FileText className="w-5 h-5 text-blue-500" />
                <span className="text-xs font-medium">Schede</span>
                <span className="text-[10px] text-muted-foreground">3 disponibili</span>
              </button>
              <button className="flex flex-col items-center gap-2 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                <Target className="w-5 h-5 text-green-500" />
                <span className="text-xs font-medium">Esercizi</span>
                <span className="text-[10px] text-muted-foreground">Prossimamente</span>
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PercorsiPage;
